import {
  DesignSystemMentionPath,
  type DesignSystemMention,
  type DesignSystemMentionTarget,
  type DesignSystemSlug,
} from "@afar-research/contracts";
import { fromYaml } from "@afar-research/shared/schemaYaml";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";

const isDesignSystemMentionPath = Schema.is(DesignSystemMentionPath);
const decodeFrontmatter = Schema.decodeUnknownOption(fromYaml(Schema.Unknown));

export function isCanonicalDesignSystemMentionPath(path: string): boolean {
  return isDesignSystemMentionPath(path);
}

/**
 * The storage slug a Design System Mention names. `DesignSystemMentionPath` is
 * pattern-locked to `.afar-research/design/design-system/<dir>/DESIGN.md`, and
 * `<dir>` *is* the storage slug: `create-design` writes `design-system/${slug}/`
 * and the Design skills reject any `DESIGN.md` whose frontmatter slug disagrees
 * with its directory. So a Design Operation never resolves its target against
 * the read model — it reads the slug straight off the path it was handed.
 *
 * Returns `null` for a path that is not a canonical mention path, which is the
 * only way a caller can be handed one: mentions come from
 * `projects.discoverDesignSystemMentions`, but a submission that survived a
 * restart is only as trustworthy as the schema that decoded it.
 */
export function designSystemSlugFromMentionPath(path: string): DesignSystemSlug | null {
  if (!isDesignSystemMentionPath(path)) return null;
  const segments = path.split("/");
  // `.afar-research / design / design-system / <slug> / DESIGN.md`
  return (segments[segments.length - 2] ?? null) as DesignSystemSlug | null;
}

function frontmatterContents(contents: string): string | null {
  const normalized = contents.replace(/^\uFEFF/, "").replaceAll("\r\n", "\n");
  if (!normalized.startsWith("---\n")) return null;
  const closing = /\n---(?:\n|$)/.exec(normalized.slice(4));
  if (closing === null) return null;
  const end = closing.index + 4;
  return normalized.slice(4, end);
}

function readFrontmatterRecord(contents: string): Record<string, unknown> | null {
  const decoded = Option.getOrNull(decodeFrontmatter(contents));
  return decoded !== null && typeof decoded === "object" && !Array.isArray(decoded)
    ? (decoded as Record<string, unknown>)
    : null;
}

function readTopLevelString(record: Record<string, unknown>, key: "id" | "name" | "description") {
  if (!Object.hasOwn(record, key)) return null;
  const value = record[key];
  return typeof value === "string" ? value : null;
}

export function readDesignSystemMentionMetadata(input: {
  readonly directoryName: string;
  readonly contents: string;
}): Pick<DesignSystemMentionTarget, "id" | "name" | "description"> {
  const fallbackName = input.directoryName;
  const contents = frontmatterContents(input.contents);
  if (contents === null) return { name: fallbackName, description: null };
  const metadata = readFrontmatterRecord(contents);
  if (metadata === null) return { name: fallbackName, description: null };

  const authoredName = readTopLevelString(metadata, "name")?.trim() ?? "";
  const authoredDescription = readTopLevelString(metadata, "description")?.trim() ?? "";
  // The identity a rename preserves. A Design System written before `id` was
  // recorded simply has none, and is matched by path like any other.
  const authoredId = readTopLevelString(metadata, "id")?.trim() ?? "";
  return {
    ...(authoredId.length > 0 && authoredId.length <= 255 ? { id: authoredId } : {}),
    name: authoredName.length > 0 && authoredName.length <= 255 ? authoredName : fallbackName,
    description:
      authoredDescription.length > 0 &&
      authoredDescription.length <= 240 &&
      !/[\r\n]/.test(authoredDescription)
        ? authoredDescription
        : null,
  };
}

/**
 * What a held Design System Mention points at *now*.
 *
 * A mention in a sent message is a record of what the user pointed at and stays
 * as it was (ADR 0116). A mention still in the composer is the target of the
 * next send, and the Design System under it may have been renamed since — which
 * moves its directory — or deleted. Identity is what a rename preserves, so it
 * is what this follows; a mention picked before mentions carried one falls back
 * to its path, which a rename invalidates and this reports as missing.
 */
export function resolveDesignSystemMention(input: {
  readonly mention: DesignSystemMention;
  readonly targets: ReadonlyArray<DesignSystemMentionTarget>;
}): {
  readonly state: "current" | "renamed" | "missing";
  readonly mention: DesignSystemMention;
} {
  const target =
    (input.mention.id === undefined
      ? undefined
      : input.targets.find((candidate) => candidate.id === input.mention.id)) ??
    input.targets.find((candidate) => candidate.path === input.mention.path);
  if (target === undefined) return { state: "missing", mention: input.mention };
  const resolved: DesignSystemMention = {
    type: "design-system",
    ...(target.id === undefined ? {} : { id: target.id }),
    name: target.name,
    path: target.path,
  };
  const unchanged = target.name === input.mention.name && target.path === input.mention.path;
  return { state: unchanged ? "current" : "renamed", mention: resolved };
}

function mentionSearchScore(target: DesignSystemMentionTarget, query: string): number | null {
  if (!query) return 0;
  const name = target.name.toLocaleLowerCase();
  const description = target.description?.toLocaleLowerCase() ?? "";
  if (name === query) return 0;
  if (name.startsWith(query)) return 1_000;
  if (name.includes(query)) return 2_000;
  if (description.includes(query)) return 3_000;
  if ("design system".includes(query)) return 4_000;
  return null;
}

export function searchDesignSystemMentionTargets(
  targets: ReadonlyArray<DesignSystemMentionTarget>,
  query: string,
  selectedPaths: ReadonlyArray<string> = [],
): DesignSystemMentionTarget[] {
  const normalizedQuery = query.trim().toLocaleLowerCase();
  if (!normalizedQuery) return [...targets];
  const selected = new Set(selectedPaths);
  return targets
    .map((target, index) => ({
      target,
      index,
      score: mentionSearchScore(target, normalizedQuery),
    }))
    .filter((entry) => entry.score !== null || selected.has(entry.target.path))
    .toSorted((left, right) => {
      const scoreDelta = (left.score ?? 5_000) - (right.score ?? 5_000);
      return scoreDelta !== 0 ? scoreDelta : left.index - right.index;
    })
    .map((entry) => entry.target);
}

export function addDesignSystemMention(
  mentions: ReadonlyArray<DesignSystemMention>,
  candidate: DesignSystemMention,
): DesignSystemMention[] {
  if (mentions.some((mention) => mention.path === candidate.path)) return [...mentions];
  // The identity comes along: it is what lets a held mention follow a rename.
  return [
    ...mentions,
    {
      type: "design-system",
      ...(candidate.id === undefined ? {} : { id: candidate.id }),
      name: candidate.name,
      path: candidate.path,
    },
  ];
}
